import React, { useState, useEffect } from 'react';
import { api } from '../api';

const ArchiveManagement = ({ onClose = null, user = null }) => {
  const [archives, setArchives] = useState([]);
  const [filteredArchives, setFilteredArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilters, setSearchFilters] = useState({
    searchTerm: '',
    meetingType: 'all',
    dateRange: 'all',
    status: 'all'
  });
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [editingArchive, setEditingArchive] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadArchives();
  }, []);

  useEffect(() => {
    filterArchives();
  }, [archives, searchFilters]);

  // Proper scroll lock management for modals
  useEffect(() => {
    if (!showEditModal && !showDetails) return;
    
    const prevOverflow = document.body.style.overflow;
    const prevPos = document.body.style.position;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'relative'; // avoid sticky side effects
    
    return () => {
      document.body.style.overflow = prevOverflow || '';
      document.body.style.position = prevPos || '';
    };
  }, [showEditModal, showDetails]);

  const loadArchives = async () => {
    setLoading(true);
    try {
      // Load archives for ALL meeting types (BOD and all committees)
      const bodResponse = await api.get('/api/vote/archived-search?meeting_type=BOD');
      const bodArchives = bodResponse.data?.archives || [];
      
      // Get all committees to load their archives
      const committeesResponse = await api.get(`/api/hoa/${user?.hoa_id}/committees`);
      const committees = committeesResponse.data?.committees || [];
      
      let committeeArchives = [];
      
      // Query for standard committees using their codes
      const standardCommitteeTypes = ['ARC', 'FINES', 'ANNUAL', 'SPECIAL'];
      for (const type of standardCommitteeTypes) {
        try {
          const response = await api.get(`/api/vote/archived-search?meeting_type=${type}`);
          const archives = response.data?.archives || [];
          committeeArchives = [...committeeArchives, ...archives];
        } catch (err) {
          console.error(`Error loading archives for ${type}:`, err);
        }
      }
      
      // Query for custom committees using their generated codes
      for (const committee of committees) {
        if (!['ARC Committee', 'Grievance Committee'].includes(committee.name)) {
          try {
            // Generate same value as frontend getMeetingTypes does
            const meetingType = committee.name.toUpperCase().replace(/\s+/g, '_');
            const response = await api.get(`/api/vote/archived-search?meeting_type=${encodeURIComponent(meetingType)}`);
            const archives = response.data?.archives || [];
            committeeArchives = [...committeeArchives, ...archives];
          } catch (err) {
            console.error(`Error loading archives for ${committee.name}:`, err);
          }
        }
      }
      
      // Combine all archives and remove duplicates
      const allArchives = [...bodArchives, ...committeeArchives];
      const uniqueArchives = Array.from(new Map(allArchives.map(arch => [arch.id, arch])).values());
      setArchives(uniqueArchives);
    } catch (error) {
      console.error('Error loading archives:', error);
      setArchives([]);
    } finally {
      setLoading(false);
    }
  };

  const filterArchives = () => {
    let filtered = [...archives];

    // Filter by search term (motions, discussions, member names)
    if (searchFilters.searchTerm) {
      const term = searchFilters.searchTerm.toLowerCase();
      filtered = filtered.filter(archive => {
        // Search in motions
        const motionMatch = Array.isArray(archive.motions) && archive.motions.some(motion => 
          motion.title?.toLowerCase().includes(term) ||
          motion.description?.toLowerCase().includes(term)
        );
        
        // Search in discussions
        const discussionMatch = Array.isArray(archive.discussions) && archive.discussions.some(discussion =>
          discussion.topic?.toLowerCase().includes(term) ||
          discussion.member_name?.toLowerCase().includes(term) ||
          discussion.notes?.toLowerCase().includes(term)
        );
        
        // Search in attendance
        const attendanceMatch = Array.isArray(archive.attendance) && archive.attendance.some(member =>
          member.name?.toLowerCase().includes(term)
        );
        
        return motionMatch || discussionMatch || attendanceMatch || 
               archive.meeting_type?.toLowerCase().includes(term);
      });
    }

    // Filter by meeting type
    if (searchFilters.meetingType !== 'all') {
      filtered = filtered.filter(archive => 
        archive.meeting_type?.toLowerCase() === searchFilters.meetingType.toLowerCase()
      );
    }

    // Filter by date range
    if (searchFilters.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (searchFilters.dateRange) {
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          filterDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtered = filtered.filter(archive => 
        new Date(archive.archived_at) >= filterDate
      );
    }

    // Filter by status (resolved/unresolved items)
    if (searchFilters.status !== 'all') {
      filtered = filtered.filter(archive => {
        if (searchFilters.status === 'unresolved') {
          const hasUnresolvedMotions = Array.isArray(archive.motions) && archive.motions.some(m => 
            ['pending', 'tabled', 'unresolved'].includes(m.status)
          );
          const hasUnresolvedDiscussions = Array.isArray(archive.discussions) && archive.discussions.some(d => 
            ['pending', 'tabled', 'unresolved'].includes(d.status)
          );
          return hasUnresolvedMotions || hasUnresolvedDiscussions;
        } else if (searchFilters.status === 'resolved') {
          const allMotionsResolved = !Array.isArray(archive.motions) || archive.motions.every(m => 
            ['approved', 'rejected', 'completed'].includes(m.status)
          );
          const allDiscussionsResolved = !Array.isArray(archive.discussions) || archive.discussions.every(d => 
            ['resolved', 'completed', 'closed'].includes(d.status)
          );
          return allMotionsResolved && allDiscussionsResolved;
        }
        return true;
      });
    }

    setFilteredArchives(filtered);
  };

  const rollForwardItems = async (archiveId, meetingType) => {
    try {
      const response = await api.post('/api/vote/rollover-session', {
        archive_id: archiveId,
        meeting_type: meetingType
      });
      
      if (response.data.success) {
        alert(`Successfully rolled forward ${response.data.rolled_discussions || 0} discussions and ${response.data.rolled_motions || 0} motions to current session.`);
        onClose && onClose(); // Close archive modal to show live voting with rolled forward items
      }
    } catch (error) {
      console.error('Error rolling forward items:', error);
      alert('Failed to roll forward items. Please try again.');
    }
  };

  const sendForApproval = async (archive) => {
    try {
      const response = await api.post('/api/vote/send-for-approval', {
        archive_id: archive.id,
        meeting_type: archive.meeting_type
      });
      
      if (response.data.success) {
        alert('Meeting minutes sent for electronic signature approval. Once approved, they will appear in the Documents tab.');
        loadArchives(); // Refresh to show updated status
      }
    } catch (error) {
      console.error('Error sending for approval:', error);
      alert('Failed to send for approval. Please try again.');
    }
  };

  return (
    <div className={onClose ? "fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" : ""}>
      <div className={onClose ? "w-full max-w-6xl bg-white rounded-2xl shadow-xl max-h-[90dvh] overflow-hidden flex flex-col" : "w-full bg-white rounded-lg border border-gray-200 max-h-[600px] overflow-hidden flex flex-col"}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">📚 Meeting Archives</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              ✕
            </button>
          )}
        </div>

        {/* Search Filters */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search Term */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                placeholder="Search motions, discussions, members..."
                value={searchFilters.searchTerm}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            {/* Meeting Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Type
              </label>
              <select
                value={searchFilters.meetingType}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, meetingType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Types</option>
                <option value="BOD">Board of Directors</option>
                <option value="ARC">Architectural Review</option>
                <option value="FINES">Grievance Committee</option>
                <option value="ANNUAL">Annual Meeting</option>
                <option value="SPECIAL">Special Meeting</option>
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <select
                value={searchFilters.dateRange}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Time</option>
                <option value="week">Past Week</option>
                <option value="month">Past Month</option>
                <option value="quarter">Past 3 Months</option>
                <option value="year">Past Year</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={searchFilters.status}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Items</option>
                <option value="unresolved">Has Unresolved Items</option>
                <option value="resolved">All Items Resolved</option>
              </select>
            </div>
          </div>
        </div>

        {/* Archives List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading archives...</p>
            </div>
          ) : filteredArchives.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📁</div>
              <p>No archived meetings found matching your criteria.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredArchives.map((archive) => (
                <div key={archive.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">
                          {archive.meeting_type || 'Regular'} Meeting
                        </h3>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {new Date(archive.archived_at).toLocaleDateString()}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                          {archive.motions?.length || 0} motions, {archive.discussions?.length || 0} discussions
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-3">
                        <div>📋 Attendance: {Array.isArray(archive.attendance) ? archive.attendance.filter(a => a.present).length : 0} present</div>
                        {Array.isArray(archive.motions) && archive.motions.length > 0 && (
                          <div>🗳️ Motions: {archive.motions.map(m => m.title).join(', ')}</div>
                        )}
                        {Array.isArray(archive.discussions) && archive.discussions.length > 0 && (
                          <div>💬 Discussions: {archive.discussions.map(d => d.topic).join(', ')}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => {
                          setSelectedArchive(archive);
                          setShowDetails(true);
                        }}
                        className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700"
                      >
                        View Details
                      </button>
                      
                      {/* Edit button for Secretary */}
                      {user && user.role && ['secretary', 'platform_admin'].includes(user.role.toLowerCase()) && (
                        <button
                          onClick={() => {
                            setEditingArchive(archive);
                            setShowEditModal(true);
                          }}
                          className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                        >
                          ✏️ Edit Minutes
                        </button>
                      )}
                      
                      <button
                        onClick={() => rollForwardItems(archive.id, archive.meeting_type)}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        🔄 Roll Forward
                      </button>
                      
                      <button
                        onClick={() => sendForApproval(archive)}
                        className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                      >
                        ✅ Send for Approval
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Archive Details Modal */}
        {showDetails && selectedArchive && (
          <ArchiveDetailsModal
            archive={selectedArchive}
            onClose={() => {
              setShowDetails(false);
              setSelectedArchive(null);
            }}
          />
        )}

        {/* Edit Meeting Minutes Modal */}
        {showEditModal && editingArchive && (
          <div 
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            style={{overflowY: 'hidden'}}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEditModal(false);
                setEditingArchive(null);
              }
            }}
          >
            <div className="bg-white rounded-lg w-full max-w-5xl h-[90dvh] flex flex-col">
              <div className="flex-shrink-0 bg-white border-b p-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold">✏️ Edit Meeting Minutes - {editingArchive.meeting_type} ({new Date(editingArchive.date).toLocaleDateString()})</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingArchive(null);
                    document.body.style.overflow = '';
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Action Items Section */}
                <div>
                  <h4 className="font-semibold mb-3">📋 Action Items</h4>
                  <div className="space-y-2">
                    {(editingArchive.action_items || []).map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const newItems = [...(editingArchive.action_items || [])];
                            newItems[index] = e.target.value;
                            setEditingArchive({...editingArchive, action_items: newItems});
                          }}
                          className="flex-1 p-2 border border-gray-300 rounded"
                        />
                        <button
                          onClick={() => {
                            const newItems = (editingArchive.action_items || []).filter((_, i) => i !== index);
                            setEditingArchive({...editingArchive, action_items: newItems});
                          }}
                          className="px-2 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                        >
                          ❌
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newItems = [...(editingArchive.action_items || []), ''];
                        setEditingArchive({...editingArchive, action_items: newItems});
                      }}
                      className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                    >
                      ➕ Add Action Item
                    </button>
                  </div>
                </div>

                {/* Meeting Notes Section */}
                <div>
                  <h4 className="font-semibold mb-3">📝 Meeting Notes</h4>
                  <textarea
                    value={editingArchive.meeting_notes || ''}
                    onChange={(e) => setEditingArchive({...editingArchive, meeting_notes: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded h-32"
                    placeholder="Add additional meeting notes..."
                  />
                </div>

                {/* Motions Section */}
                <div>
                  <h4 className="font-semibold mb-3">🗳️ Motions</h4>
                  <div className="space-y-3">
                    {Array.isArray(editingArchive.motions) && editingArchive.motions.map((motion, index) => (
                      <div key={index} className="border border-gray-200 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <input
                            type="text"
                            value={motion.title || ''}
                            onChange={(e) => {
                              const newMotions = [...editingArchive.motions];
                              newMotions[index] = {...motion, title: e.target.value};
                              setEditingArchive({...editingArchive, motions: newMotions});
                            }}
                            className="flex-1 p-2 border border-gray-300 rounded mr-2"
                            placeholder="Motion title"
                          />
                          <button
                            onClick={() => {
                              const newMotions = editingArchive.motions.filter((_, i) => i !== index);
                              setEditingArchive({...editingArchive, motions: newMotions});
                            }}
                            className="px-2 py-1 bg-red-500 text-white rounded text-sm"
                          >
                            🗑️
                          </button>
                        </div>
                        <textarea
                          value={motion.description || ''}
                          onChange={(e) => {
                            const newMotions = [...editingArchive.motions];
                            newMotions[index] = {...motion, description: e.target.value};
                            setEditingArchive({...editingArchive, motions: newMotions});
                          }}
                          className="w-full p-2 border border-gray-300 rounded mb-2"
                          placeholder="Motion description"
                          rows={2}
                        />
                        <select
                          value={motion.status || 'pending'}
                          onChange={(e) => {
                            const newMotions = [...editingArchive.motions];
                            newMotions[index] = {...motion, status: e.target.value};
                            setEditingArchive({...editingArchive, motions: newMotions});
                          }}
                          className="p-2 border border-gray-300 rounded"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                          <option value="tabled">Tabled</option>
                        </select>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newMotions = [...(editingArchive.motions || []), {title: '', description: '', status: 'pending'}];
                        setEditingArchive({...editingArchive, motions: newMotions});
                      }}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                    >
                      ➕ Add Motion
                    </button>
                  </div>
                </div>

                {/* Save Changes */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingArchive(null);
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await api.put(`/api/vote/archive/${editingArchive.id}`, editingArchive);
                        alert('Meeting minutes updated successfully!');
                        setShowEditModal(false);
                        setEditingArchive(null);
                        loadArchives(); // Refresh the list
                      } catch (error) {
                        console.error('Failed to update meeting minutes:', error);
                        alert('Failed to update meeting minutes. Please try again.');
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    💾 Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Archive Details Modal Component
const ArchiveDetailsModal = ({ archive, onClose }) => {
  React.useEffect(() => {
    // Prevent body scrolling when modal is open - save previous state
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'relative';
    return () => {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = prevOverflow || '';
      document.body.style.position = prevPosition || '';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl h-[90dvh] flex flex-col">
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">
            {archive.meeting_type || 'Regular'} Meeting - {new Date(archive.archived_at).toLocaleDateString()}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-2xl text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Meeting Location */}
          {archive.meeting_location && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">📍 Meeting Location</h4>
              <p className="text-gray-700">{archive.meeting_location}</p>
            </div>
          )}
          
          {/* Rejection/Deletion Reason */}
          {archive.rejection_reason && ['rejected', 'deleted'].includes(archive.approval_status) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 mb-2">🚫 {archive.approval_status === 'deleted' ? 'Deletion' : 'Rejection'} Reason</h4>
              <p className="text-red-800">{archive.rejection_reason}</p>
            </div>
          )}
          
          {/* Attendance Section */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">👥 Attendance</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.isArray(archive.attendance) && archive.attendance.map((member, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">{member.name}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    member.present ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {member.present ? 'Present' : 'Absent'}
                  </span>
                </div>
              )) || <p className="text-gray-500">No attendance records</p>}
            </div>
          </div>

          {/* Motions Section */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">🗳️ Motions</h4>
            <div className="space-y-3">
              {Array.isArray(archive.motions) && archive.motions.map((motion, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">{motion.title}</h5>
                      <p className="text-sm text-gray-600 mt-1">{motion.description}</p>
                      
                      {/* Discussion Notes */}
                      {motion.discussion && (
                        <div className="mt-2 p-2 bg-blue-50 rounded">
                          <p className="text-sm font-medium text-blue-900">Discussion:</p>
                          <p className="text-sm text-blue-800">{motion.discussion}</p>
                        </div>
                      )}
                      
                      {/* Proposer and Seconder */}
                      {(motion.proposer_name || motion.seconder_name) && (
                        <div className="mt-2 text-sm text-purple-700">
                          {motion.proposer_name && <p><strong>Proposed by:</strong> {motion.proposer_name}</p>}
                          {motion.seconder_name && <p><strong>Seconded by:</strong> {motion.seconder_name}</p>}
                        </div>
                      )}
                      
                      {/* Vote Counts */}
                      {motion.vote_counts && (
                        <div className="text-xs text-gray-500 mt-2">
                          Votes: Approve {motion.vote_counts.approve || 0}, 
                          Oppose {motion.vote_counts.oppose || 0}, 
                          Abstain {motion.vote_counts.abstain || 0}
                        </div>
                      )}
                      
                      {/* Individual Votes */}
                      {motion.individual_votes && (motion.individual_votes.approve?.length > 0 || motion.individual_votes.oppose?.length > 0 || motion.individual_votes.abstain?.length > 0) && (
                        <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                          <p className="font-medium text-gray-900 mb-2">Individual Votes:</p>
                          {motion.individual_votes.approve && motion.individual_votes.approve.length > 0 && (
                            <p className="text-green-700"><strong>Voted For:</strong> {motion.individual_votes.approve.map(v => v.name).join(', ')}</p>
                          )}
                          {motion.individual_votes.oppose && motion.individual_votes.oppose.length > 0 && (
                            <p className="text-red-700"><strong>Voted Against:</strong> {motion.individual_votes.oppose.map(v => v.name).join(', ')}</p>
                          )}
                          {motion.individual_votes.abstain && motion.individual_votes.abstain.length > 0 && (
                            <p className="text-gray-600"><strong>Abstained:</strong> {motion.individual_votes.abstain.map(v => v.name).join(', ')}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ml-2 ${
                      motion.status === 'approved' ? 'bg-green-100 text-green-800' :
                      motion.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {motion.status === 'approved' ? 'Passed' : motion.status === 'rejected' ? 'Failed' : motion.status || 'Pending'}
                    </span>
                  </div>
                </div>
              )) || <p className="text-gray-500">No motions recorded</p>}
            </div>
          </div>

          {/* Discussions Section */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">💬 Discussions</h4>
            <div className="space-y-3">
              {Array.isArray(archive.discussions) && archive.discussions.map((discussion, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900">{discussion.topic}</h5>
                      <p className="text-sm text-gray-600">By: {discussion.member_name}</p>
                      {discussion.notes && (
                        <p className="text-sm text-gray-700 mt-2">{discussion.notes}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      discussion.status === 'resolved' ? 'bg-green-100 text-green-800' :
                      discussion.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {discussion.status || 'Pending'}
                    </span>
                  </div>
                </div>
              )) || <p className="text-gray-500">No discussions recorded</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchiveManagement;